import moment from 'moment';
import _, { divide } from 'lodash';
import { Button, DatePicker, TimePicker } from 'antd';
import React, { useState, useMemo } from 'react';
import { useDebounceEffect } from 'ahooks';
import memo from '../../component/memo';
import { jsonataFactory } from '@/utils';
import useMetaStore from '@/store/useMetaStore';

const { RangePicker } = DatePicker;

function SPDatePicker(props: {
  isSystem?: boolean;
  value?: any;
  showTime?: boolean;
  onlyTime?: boolean;
  /** 来源，在表格中或者搜索中 */
  from?: string;
  onChange?: any;
  allowClear?: boolean;
  config?: any;
  isValidateDate?: boolean;
  validateDateStart?: string;
  validateDateEnd?: string;
  rangePicker?: boolean;
}) {
  const {
    isSystem,
    value,
    from,
    onlyTime,
    allowClear,
    config,
    isValidateDate,
    validateDateStart,
    validateDateEnd,
    rangePicker,
  } = props;
  const { pageType } = useMetaStore();

  // 第一次加载，判定是否有值
  useDebounceEffect(() => {
    // add时以当前时间更新value，edit时不变
    if (isSystem && from !== 'search' && pageType === 'add') {
      const isCurrentTime = new Date(value).valueOf() === new Date().valueOf();
      if (!isCurrentTime) {
        props?.onChange(moment(new Date()));
      }
    }
  }, [props], { wait: 500 });

  // 生成开始时间
  const startDateStr = !!validateDateStart && _.get(config?.storeValue, validateDateStart)?.toString?.();
  const startDate = useMemo(() => {
    try {
      // 可能 validateDateStart 但是 startDateStr 存在，如$now()
      if (validateDateStart || startDateStr) {
        const v = jsonataFactory(validateDateStart).evaluate(config.storeValue);
        return moment.isMoment(v) ? v : moment(v);
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  }, [validateDateStart, startDateStr]);

  // 生成结束时间
  const endDateStr = !!validateDateEnd && _.get(config?.storeValue, validateDateEnd)?.toString?.();
  const endDate = useMemo(() => {
    try {
      if (validateDateEnd && endDateStr) {
        const v = jsonataFactory(validateDateEnd).evaluate(config.storeValue);
        return moment.isMoment(v) ? v : moment(v);
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  }, [validateDateEnd, endDateStr]);

  // 可选时间设置
  const disabledDateFunc = (current) => {
    if (!isValidateDate) {
      return false;
    }
    const conditions = [];
    if (startDate) {
      conditions.push(current < startDate);
    }
    if (endDate) {
      conditions.push(current > endDate);
    }
    if (conditions.length > 0) {
      return conditions.every((x) => x === true);
    }
    return false;
  };

  if (from === 'search' || rangePicker) {
    let v;
    if (_.isArray(value) && _.isString(value[0])) {
      v = [moment(value[0]), moment(value[1])];
    } else if (_.isArray(value) && moment.isMoment(value[0])) {
      v = value;
    }
    return <RangePicker {...props} value={v as any} allowClear={allowClear !== false} style={{ width: '100%' }} disabledDate={disabledDateFunc} />;
  } else {
    const v = value ? moment(value) : null;
    if (onlyTime) {
      return (
        <TimePicker {...props} value={v} style={{ width: '100%' }} allowClear={allowClear !== false} />
      );
    }
    return (
      <DatePicker {...props} value={v} style={{ width: '100%' }} allowClear={allowClear !== false} disabledDate={disabledDateFunc} />
    );
  }
}

// export default memo(SPDatePicker);

export default React.memo(SPDatePicker, (prevProps: any, nextProps: any) => {
  if (prevProps.isValidateDate) {
    if (prevProps.validateDateStart && (
      _.get(prevProps.config?.storeValue, prevProps.validateDateStart)?.toString()
      !== _.get(nextProps.config?.storeValue, nextProps.validateDateStart)?.toString()
    )) {
      return false;
    }
    if (prevProps.validateDateEnd && (
      _.get(prevProps.config?.storeValue, prevProps.validateDateEnd)?.toString()
      !== _.get(nextProps.config?.storeValue, nextProps.validateDateEnd)?.toString()
    )) {
      return false;
    }
  }
  const prevPropsValue = moment.isMoment(prevProps.value) ? prevProps.value.toString() : prevProps.value;
  const nextPropsValue = moment.isMoment(nextProps.value) ? nextProps.value.toString() : nextProps.value;
  if (
    (prevProps.onlyDate !== nextProps.onlyDate) ||
    (prevProps.onlyTime !== nextProps.onlyTime) ||
    (prevProps.validateDateEnd !== nextProps.validateDateEnd) ||
    (prevProps.validateDateStart !== nextProps.validateDateStart) ||
    (prevProps.format !== nextProps.format) ||
    (prevProps.isSystem !== nextProps.isSystem) ||
    (prevProps.rangePicker !== nextProps.rangePicker)
  ) {
    return false;
  }

  if (prevPropsValue === nextPropsValue && prevProps.disabled === nextProps.disabled) {
    return true;
  }

  return false;
});
